import {Decimal} from 'decimal.js';
import {
  Button,
  Card,
  Choice,
  EmptyStatus,
  Form,
  FormButton,
  FormField,
  IconButton,
  Segment,
  Separator,
  Switch,
  openConfirmDialog,
  showNotification,
  type FormControl,
  type FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import styled from 'styled-components';
import {z} from 'zod';

import {useRef, type ReactNode} from 'react';
import {useController, useFormState, useWatch} from 'react-hook-form';

import {
  useCreatePackageMutation,
  useUpdateFeatureMutation,
  useUpdatePackageMutation,
  type FeatureResponseBody,
  type PackageResponseBody,
} from '@dms/api/new-vehicle';
import {MoneyText, getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import type {ApiError} from 'shared/web';

import {handleCatalogueError, joinParts, t} from './catalogueUi';
import {GrossPriceField} from './GrossPriceField';
import {priceInput} from './moneyInput';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {packageSchema} from './packageSchema';
import {PriceRow} from './PriceRow';
import {useCatalogueVatRate} from './useCatalogueVatRate';
import {usePublishedPriceSave} from './usePublishedPriceSave';

type BuilderValues = z.infer<typeof packageSchema>;
type Control = FormControl<BuilderValues>;

type Props = {
  modelYearId: string;
  item?: PackageResponseBody;
  features: FeatureResponseBody[];
  trims: {id: string; name: string}[];
  currency: string;
  canViewCost: boolean;
  onClose: () => void;
};

/** Full-width editor for one package: basics and price, its equipment, and the trims it is sold on. */
export function PackageBuilder(props: Props) {
  const [create] = useCreatePackageMutation();
  const [update] = useUpdatePackageMutation();
  const [updateFeature] = useUpdateFeatureMutation();
  const persisted = useRef<{id: string; version: number; priceNet: string} | null>(null);
  const featureCheckpoints = useRef(
    new Map<string, {standaloneOrderable: boolean; version: number}>()
  );
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const defaultValues: BuilderValues = {
    name: props.item?.name ?? '',
    factoryCode: props.item?.codes[0] ?? '',
    description: props.item?.description ?? '',
    status: props.item?.status === 'active' ? 'active' : 'draft',
    priceNet: props.item?.priceNet ?? '0.00',
    costNet: props.item?.costNet ?? '',
    memberFeatureIds: props.item?.memberFeatureIds ?? [],
    applicableTrimIds: props.item?.applicableTrimIds ?? [],
    packageOnlyFeatureIds: props.features
      .filter((feature) => !feature.standaloneOrderable)
      .map(({id}) => id),
  };

  const save = (values: BuilderValues) => {
    const taxonomyChanges = props.features.filter((feature) => {
      if (!values.memberFeatureIds.includes(feature.id)) {
        return false;
      }
      const desiredStandaloneOrderable = !values.packageOnlyFeatureIds.includes(feature.id);
      return (
        (featureCheckpoints.current.get(feature.id)?.standaloneOrderable ??
          feature.standaloneOrderable) !== desiredStandaloneOrderable
      );
    });
    const body = {
      modelYearId: props.modelYearId,
      name: values.name,
      codes: values.factoryCode
        ? [values.factoryCode, ...(props.item?.codes.slice(1) ?? [])]
        : (props.item?.codes.slice(1) ?? []),
      description: values.description,
      priceNet: values.priceNet,
      costNet: props.canViewCost ? values.costNet || null : props.item?.costNet,
      memberFeatureIds: values.memberFeatureIds,
      applicableTrimIds: values.applicableTrimIds,
      status: values.status,
      version: persisted.current?.version ?? props.item?.version ?? 1,
    };
    const packageId = persisted.current?.id ?? props.item?.id;
    return (
      packageId
        ? update({packageId, packageWriteRequestBody: body})
        : create({packageWriteRequestBody: body})
    )
      .unwrap()
      .then(async (savedPackage) => {
        const {id, version, priceNet} = savedPackage;
        persisted.current = {id, version, priceNet};
        for (const feature of taxonomyChanges) {
          const checkpoint = featureCheckpoints.current.get(feature.id);
          const desiredStandaloneOrderable = !values.packageOnlyFeatureIds.includes(feature.id);
          const savedFeature = await updateFeature({
            featureId: feature.id,
            featureWriteRequestBody: {
              name: feature.name,
              codes: feature.codes,
              kind: feature.kind,
              fitStage: feature.fitStage,
              standaloneOrderable: desiredStandaloneOrderable,
              category: feature.category,
              factoryCode: feature.factoryCode,
              defaultPriceNet: feature.defaultPriceNet,
              costNet: feature.costNet ?? null,
              version: checkpoint?.version ?? feature.version,
            },
          }).unwrap();
          featureCheckpoints.current.set(feature.id, {
            standaloneOrderable: savedFeature.standaloneOrderable,
            version: savedFeature.version,
          });
        }
      });
  };
  const submit: FormSubmitHandler<BuilderValues> = (values, _setErrors, _reset, blocker) => {
    const checkpoint = persisted.current;
    // A package persisted by a half-failed save is an existing one now, so its price is stored.
    const packageId = checkpoint?.id ?? props.item?.id;
    return (
      packageId
        ? publishedPrice.save({
            values,
            storedValues: {
              ...defaultValues,
              priceNet: checkpoint?.priceNet ?? defaultValues.priceNet,
            },
            priceField: 'priceNet',
            isSavePending: checkpoint !== null,
            scope: 'package',
            resourceId: packageId,
            save,
            onDone: props.onClose,
          })
        : save(values).then(() => {
            props.onClose();
            return false;
          })
    )
      .then((isConfirming) => {
        if (isConfirming) {
          // Keeps the unsaved-changes blocker from leaving while the price waits for confirmation.
          blocker?.reportApiFailure?.();
          return;
        }
        showNotification.success(t('general.notifications.changesSaved', 'Changes saved'));
      })
      .catch((error: ApiError) => {
        // Each package write that succeeds replaces the checkpoint.
        if (persisted.current !== checkpoint) {
          showNotification.error(
            t(
              'entity.newVehicle.catalogue.packageSavedFeatureFailed',
              'The package was saved, but some equipment settings were not. Retry to finish saving — the package will not be created again.'
            )
          );
        } else {
          handleCatalogueError(error);
        }
        blocker?.reportApiFailure?.();
      });
  };

  return (
    <Form<BuilderValues>
      key={props.item?.id ?? 'new'}
      experimentalZodSchema={packageSchema}
      shouldWatchForUnsavedChanges
      defaultValues={defaultValues}
      onSubmit={submit}
    >
      {(control) => (
        <VStack spacing={4}>
          <BuilderHeader control={control} item={props.item} onClose={props.onClose} />
          <BuilderColumns>
            <BasicsCard control={control} />
            <ContentsCard control={control} features={props.features} currency={props.currency} />
            <SummaryColumn>
              <PricingCard
                control={control}
                features={props.features}
                currency={props.currency}
                canViewCost={props.canViewCost}
                vatRate={vatRate}
              />
              <TrimsCard control={control} trims={props.trims} />
            </SummaryColumn>
          </BuilderColumns>
          <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
        </VStack>
      )}
    </Form>
  );
}

// Full width like the rest of the app: basics | contents | a narrower summary, all one height so the
// row reads as one sheet. Laptops keep the summary beside a stacked pair; smaller screens stack all.
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const BuilderColumns = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(4)};
  grid-template-columns: 3fr 3fr 2fr;
  align-items: stretch;

  @media ${({theme}) => theme.devices.notebook} {
    grid-template-columns: 2fr 1fr;

    & > :nth-child(3) {
      grid-column: 2;
      grid-row: 1 / span 2;
    }
  }

  @media ${({theme}) => theme.devices.tablet}, ${({theme}) => theme.devices.mobile} {
    grid-template-columns: 1fr;
  }
`;

// Pricing keeps its height; trims take the rest so the summary ends on the same line as its neighbours.
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const SummaryColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(4)};

  & > :last-child {
    flex: 1;
  }
`;

function BuilderHeader(props: {control: Control; item?: PackageResponseBody; onClose: () => void}) {
  const {isDirty} = useFormState({control: props.control});
  const close = () =>
    isDirty
      ? openConfirmDialog({
          text: t('entity.newVehicle.catalogue.discardPackageChanges', 'Discard unsaved changes?'),
          onConfirm: props.onClose,
        })
      : props.onClose();
  return (
    <HStack justify="space-between" align="flex-end" spacing={4} wrap>
      <VStack spacing={1} align="flex-start">
        <Button
          variant="link"
          size="small"
          leftIcon="navigation/arrow_back"
          title={t('entity.newVehicle.catalogue.backToPackages', 'All packages')}
          onClick={close}
        />
        <Heading size={3}>
          {props.item
            ? t('entity.newVehicle.catalogue.editPackageNamed', 'Edit package · {name}', {
                name: props.item.name,
              })
            : t('entity.newVehicle.catalogue.addPackage', 'Add package')}
        </Heading>
      </VStack>
      <HStack spacing={2}>
        <Button variant="secondary" title={t('general.actions.cancel', 'Cancel')} onClick={close} />
        <FormButton
          control={props.control}
          type="submit"
          variant="primary"
          title={
            props.item
              ? t('general.actions.saveChanges', 'Save changes')
              : t('entity.newVehicle.catalogue.createPackage', 'Create package')
          }
        />
      </HStack>
    </HStack>
  );
}

function BasicsCard(props: {control: Control}) {
  const status = useController({control: props.control, name: 'status'});
  return (
    <Card title={t('entity.newVehicle.catalogue.packageBasics', 'Package basics')} isFullHeight>
      <VStack spacing={4}>
        <Grid columns={[1, 3]} spacing={3}>
          <GridItem span={[1, 2]}>
            <FormField
              control={props.control}
              name="name"
              type="text"
              label={t('general.labels.name', 'Name')}
              placeholder={t(
                'entity.newVehicle.catalogue.packageNamePlaceholder',
                'e.g. Winter pack'
              )}
              isRequired
            />
          </GridItem>
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
        </Grid>
        <FormField
          control={props.control}
          name="description"
          type="textarea"
          label={t('general.labels.description', 'Description')}
          helperText={t(
            'entity.newVehicle.catalogue.packageDescriptionHelp',
            'Shown to the customer in the configurator.'
          )}
          isRequired
        />
        <HStack spacing={3} align="center" wrap>
          <Segment
            label={t('general.labels.status', 'Status')}
            value={status.field.value}
            onChange={status.field.onChange}
            options={(['draft', 'active'] as const).map((value) => ({
              value,
              label: getNewVehicleEnumLabel('packageStatus', value, t),
            }))}
          />
          <Text size="xSmall" color="secondary">
            {t(
              'entity.newVehicle.catalogue.packageStatusHelp',
              'Only active packages appear in the configurator.'
            )}
          </Text>
        </HStack>
      </VStack>
    </Card>
  );
}

function PricingCard(props: {
  control: Control;
  features: FeatureResponseBody[];
  currency: string;
  canViewCost: boolean;
  vatRate?: string;
}) {
  const [priceNet, memberIds, packageOnlyIds] = useWatch({
    control: props.control,
    name: ['priceNet', 'memberFeatureIds', 'packageOnlyFeatureIds'],
  });
  const parsedPrice = priceInput.safeParse(priceNet);
  const price = parsedPrice.success ? new Decimal(parsedPrice.data) : null;
  const members = props.features.filter(({id}) => memberIds.includes(id));
  const separately = members.filter(({id}) => !packageOnlyIds.includes(id));
  const separateTotal = separately.reduce(
    (sum, feature) => sum.plus(feature.defaultPriceNet),
    new Decimal(0)
  );
  // Compared only when there is something to compare against; package-only members have no alone price.
  const difference = price && separately.length > 0 ? separateTotal.minus(price) : null;
  return (
    <Card title={t('entity.newVehicle.catalogue.pricing', 'Pricing')}>
      <VStack spacing={4}>
        <PriceRow vatRate={props.vatRate} canViewCost={props.canViewCost} spacing={3}>
          <GrossPriceField
            control={props.control}
            name="priceNet"
            vatRate={props.vatRate}
            suffix={props.currency}
            label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
            isRequired
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              suffix={props.currency}
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
        <Show when={difference}>
          {(difference) => (
            <VStack spacing={2}>
              <Separator spacing={0} />
              <SummaryRow
                label={t(
                  'entity.newVehicle.catalogue.membersBoughtSeparately',
                  'Bought separately ({count})',
                  {count: separately.length}
                )}
              >
                <MoneyText
                  amount={separateTotal.toFixed(2)}
                  currency={props.currency}
                  size="small"
                />
              </SummaryRow>
              {difference.gt(0) ? (
                <SummaryRow
                  label={t('entity.newVehicle.catalogue.customerSaves', 'Customer saves (net)')}
                >
                  <MoneyText
                    amount={difference.toFixed(2)}
                    currency={props.currency}
                    size="small"
                    color="success"
                    alternative
                  />
                </SummaryRow>
              ) : (
                <Text size="xSmall" color="warning">
                  {t(
                    'entity.newVehicle.catalogue.packageNotCheaper',
                    'The package costs no less than buying these items separately.'
                  )}
                </Text>
              )}
            </VStack>
          )}
        </Show>
      </VStack>
    </Card>
  );
}

function SummaryRow(props: {label: string; children: ReactNode}) {
  return (
    <HStack justify="space-between" align="center" spacing={3}>
      <Text size="small" color="secondary">
        {props.label}
      </Text>
      {props.children}
    </HStack>
  );
}

function ContentsCard(props: {
  control: Control;
  features: FeatureResponseBody[];
  currency: string;
}) {
  const members = useController({control: props.control, name: 'memberFeatureIds'});
  const packageOnly = useController({control: props.control, name: 'packageOnlyFeatureIds'});
  const memberIds = members.field.value;
  const formatCurrency = useFormatCurrency();
  const available = props.features.filter(({id}) => !memberIds.includes(id));
  const setOrderableAlone = (id: string, orderable: boolean) =>
    packageOnly.field.onChange(
      orderable
        ? packageOnly.field.value.filter((value) => value !== id)
        : [...packageOnly.field.value, id]
    );
  return (
    <Card
      isFullHeight
      title={t('entity.newVehicle.catalogue.packageContents', 'Package contents ({count})', {
        count: memberIds.length,
      })}
    >
      <VStack spacing={3}>
        {memberIds.length ? (
          <VStack role="list">
            {memberIds.map((id, index) => {
              const feature = props.features.find((item) => item.id === id);
              const isOrderableAlone = !packageOnly.field.value.includes(id);
              return (
                <VStack key={id} role="listitem">
                  <Show when={index > 0}>
                    <Separator spacing={0} />
                  </Show>
                  <HStack justify="space-between" align="center" spacing={3}>
                    <Box flex={1} minWidth={0} paddingVertical={2}>
                      <VStack spacing={0}>
                        <Text size="small" alternative>
                          {feature?.name ?? id}
                        </Text>
                        <Text size="xSmall" color="secondary">
                          {joinParts([
                            feature
                              ? getNewVehicleEnumLabel('featureCategory', feature.category, t)
                              : undefined,
                            feature
                              ? getNewVehicleEnumLabel('fitStage', feature.fitStage, t)
                              : undefined,
                            isOrderableAlone && feature
                              ? t('entity.newVehicle.catalogue.aloneFor', 'alone {amount}', {
                                  amount: formatCurrency(
                                    Number(feature.defaultPriceNet),
                                    props.currency,
                                    2
                                  ),
                                })
                              : t('entity.newVehicle.rules.packageOnly', 'Package-only'),
                          ])}
                        </Text>
                      </VStack>
                    </Box>
                    <Show when={Boolean(feature)}>
                      <Switch
                        label={t('entity.newVehicle.catalogue.orderableAlone', 'Orderable alone')}
                        value={isOrderableAlone}
                        onChange={(value) => setOrderableAlone(id, value)}
                      />
                    </Show>
                    <IconButton
                      icon="navigation/close"
                      size="small"
                      aria-label={t(
                        'entity.newVehicle.catalogue.removeFromPackage',
                        'Remove {name}',
                        {
                          name: feature?.name ?? id,
                        }
                      )}
                      onClick={() =>
                        members.field.onChange(memberIds.filter((value) => value !== id))
                      }
                    />
                  </HStack>
                </VStack>
              );
            })}
          </VStack>
        ) : (
          <EmptyStatus
            headline={t('entity.newVehicle.catalogue.emptyPackageContents', 'No equipment yet')}
            subheadline={t(
              'entity.newVehicle.catalogue.emptyPackageContentsHint',
              'Add the equipment this package bundles.'
            )}
          />
        )}
        <Choice<string>
          value={null}
          isSearchable
          menuInPortal
          placeholder={t('entity.newVehicle.catalogue.addEquipmentToPackage', 'Add equipment…')}
          options={available.map(({id, name}) => ({value: id, label: name}))}
          onChange={(id) => id && members.field.onChange([...memberIds, id])}
        />
        <Show when={memberIds.length > 0}>
          <Text size="xSmall" color="secondary">
            {t(
              'entity.newVehicle.catalogue.orderableAloneHelp',
              'Switching off “Orderable alone” makes the item package-only in the equipment library, for every trim and package.'
            )}
          </Text>
        </Show>
      </VStack>
    </Card>
  );
}

function TrimsCard(props: {control: Control; trims: {id: string; name: string}[]}) {
  const applicable = useController({control: props.control, name: 'applicableTrimIds'});
  const selected = applicable.field.value;
  return (
    <Card title={t('entity.newVehicle.catalogue.applyToTrims', 'Apply to trims')} isFullHeight>
      <VStack spacing={3}>
        {props.trims.map((trim) => (
          <Switch
            key={trim.id}
            label={trim.name}
            value={selected.includes(trim.id)}
            onChange={(isOn) =>
              applicable.field.onChange(
                isOn ? [...selected, trim.id] : selected.filter((id) => id !== trim.id)
              )
            }
          />
        ))}
        <Text size="xSmall" color="secondary">
          {selected.length
            ? t(
                'entity.newVehicle.catalogue.offeredOnCount',
                'Offered on {count} of {total} trims.',
                {
                  count: selected.length,
                  total: props.trims.length,
                }
              )
            : t(
                'entity.newVehicle.catalogue.noTrimMeansAll',
                'No trim selected, so the package is offered on every trim.'
              )}
        </Text>
      </VStack>
    </Card>
  );
}
